import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Link as LinkIcon,
  Trash2,
  UserRound,
} from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { Toast } from '../../../components/ui';
import { apiFetch } from '../../../services/apiClient';
import {
  ErrorBanner,
  PersonaAvatar,
  PersonaState,
  PersonaTagChip,
} from '../components/PersonaShared';
import PersonaResultHeader from '../components/PersonaResultHeader';
import { asDetailText, buildPersonaDetail } from '../services/personaDetail';
import { personaAbTestApi, personaAssetUrl, personaApi, personaResultShareApi } from '../services/personaService';
import { downloadExportFile, sanitizeCsvFilename } from '../utils/csvExport';
import '../styles/persona.scss';

const asArray = (value) => (Array.isArray(value) ? value : []);
const EXPORT_OPTIONS = [
  { value: 'csv', label: 'CSV (.csv)' },
  { value: 'xlsx', label: 'Excel (.xlsx)' },
];
const FEEDBACK_PAGE_SIZE = 3;

const firstText = (...values) => values.find((value) => typeof value === 'string' && value.trim()) || '';

const formatResultDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const formatSharedAt = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '공유 시각을 확인할 수 없습니다.';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).replace(/\.\s?/g, '.').replace(/\.$/, '');
};

const isLikelyImageSource = (value) => {
  if (!value) return false;
  const text = String(value);
  return /^(data:image|blob:)/i.test(text) ||
    /\/api\/persona\/storage\//i.test(text) ||
    /\/api\/persona\/share-links\/[^/]+\/assets\//i.test(text) ||
    /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i.test(text);
};

const firstImageSource = (...values) => {
  for (const value of values) {
    if (value && isLikelyImageSource(value)) return value;
  }
  return '';
};

const isPersonaStorageImage = (value) => /\/api\/persona\/storage\//i.test(String(value || ''));

const getScreenImage = (screen) => (
  firstImageSource(
    screen?.image_url,
    screen?.imageUrl,
    screen?.capturedImageUrl,
    screen?.captured_image_url,
    screen?.thumbnail_url,
    screen?.thumbnailUrl,
    screen?.source_image_url,
    screen?.sourceImageUrl,
    screen?.source,
  )
);

const normalizeVariantScreen = (screen, variant, index) => ({
  ...screen,
  id: screen?.id || `${variant.toLowerCase()}-screen-${index + 1}`,
  key: screen?.key || variant,
  variant,
  label: screen?.label || `${variant} 화면`,
  name: screen?.name || screen?.pageTitle || screen?.fileName || `Flow ${index + 1}`,
  url: screen?.url || '',
  image_url: getScreenImage(screen),
});

const getContextData = (test) => test?.contextData || test?.context_data || {};

const getSourceData = (test) => {
  const contextData = getContextData(test);
  return contextData.sourceData || contextData.source_data || {};
};

const getVariantScreens = (test, variant) => {
  const sourceData = getSourceData(test);
  const variants = sourceData.variants || {};
  const direct = asArray(variants[variant] || variants[variant.toLowerCase()]);
  const sourceScreens = direct.length
    ? direct
    : asArray(test?.screens).filter((screen) => String(screen?.key || screen?.variant || '').toUpperCase() === variant);

  if (sourceScreens.length) {
    return sourceScreens.map((screen, index) => normalizeVariantScreen(screen, variant, index));
  }

  return [{
    id: `${variant.toLowerCase()}-screen-1`,
    key: variant,
    variant,
    label: `${variant} 화면`,
    name: `${variant} 화면`,
    image_url: '',
    url: '',
  }];
};

const getSourceTypeLabel = (test) => {
  const sourceType = test?.sourceType || test?.source_type || getSourceData(test).sourceType || getSourceData(test).source_type;
  if (sourceType === 'figma') return 'Figma';
  if (sourceType === 'image') return 'Image';
  if (sourceType === 'url') return 'URL';
  return sourceType || '-';
};

const getPersonaCount = (test, results) => {
  const contextData = getContextData(test);
  const selection = contextData.personaSelection || contextData.persona_selection || {};
  return test?.persona_count ||
    test?.personaCount ||
    asArray(selection.selectedPersonaIds || selection.selected_persona_ids).length ||
    asArray(contextData.persona_ids || contextData.personaIds).length ||
    results.length;
};

const normalizeWinner = (value) => {
  const winner = String(value || '').trim().toUpperCase();
  if (winner === 'A' || winner === 'B') return winner;
  return 'tie';
};

const getResultWinner = (result) => normalizeWinner(result?.scores?.winner || result?.winner || result?.selectedVariant || result?.selected_variant);

const getSummary = (test, results) => {
  const explicit = test?.summary || {};
  const computed = results.reduce((acc, result) => {
    const winner = getResultWinner(result);
    if (winner === 'A') acc.voteA += 1;
    if (winner === 'B') acc.voteB += 1;
    return acc;
  }, { voteA: 0, voteB: 0 });
  const totalVotes = Number(explicit.totalVotes ?? explicit.total_votes ?? results.length ?? 0);
  const voteA = Number(explicit.voteA ?? explicit.vote_a ?? computed.voteA);
  const voteB = Number(explicit.voteB ?? explicit.vote_b ?? computed.voteB);
  const total = totalVotes || voteA + voteB;
  let winner = normalizeWinner(explicit.winner);
  if (winner === 'tie') {
    if (voteA > voteB) winner = 'A';
    if (voteB > voteA) winner = 'B';
  }

  return {
    voteA,
    voteB,
    totalVotes: total,
    percentA: Number(explicit.percentA ?? explicit.percent_a ?? (total ? Math.round((voteA / total) * 100) : 0)),
    percentB: Number(explicit.percentB ?? explicit.percent_b ?? (total ? Math.round((voteB / total) * 100) : 0)),
    winner,
  };
};

const getPersonaSnapshot = (result, personas) => {
  const snapshot = result?.personaSnapshot || result?.persona_snapshot || {};
  const personaId = result?.persona_id ?? result?.personaId;
  const matched = personas.find((persona) => String(persona.id) === String(personaId));
  return {
    ...matched,
    ...snapshot,
    id: snapshot.id || matched?.id || personaId,
    name: snapshot.name || result?.personaName || result?.persona_name || matched?.name || (personaId ? `#${personaId}` : '퍼소나'),
  };
};

const getFeedbackText = (result) => {
  const feedback = result?.feedback;
  if (typeof feedback === 'string') return feedback;
  if (Array.isArray(feedback)) return feedback.filter(Boolean).join(' ');
  if (feedback && typeof feedback === 'object') {
    return firstText(feedback.summary, feedback.reason, feedback.overallFeedback, feedback.overall_feedback, result?.scores?.reasonForChoice, result?.scores?.reason_for_choice) || '비교 평가가 완료되었습니다.';
  }
  return firstText(result?.scores?.reasonForChoice, result?.scores?.reason_for_choice, result?.summary, result?.error_message, '비교 평가가 완료되었습니다.');
};

const getFlowSteps = (variantScreensA, variantScreensB) => {
  const maxLength = Math.max(variantScreensA.length, variantScreensB.length, 1);
  return Array.from({ length: maxLength }, (_, index) => ({
    index,
    label: `Flow ${index + 1}`,
    a: variantScreensA[index] || variantScreensA[0],
    b: variantScreensB[index] || variantScreensB[0],
  }));
};

const AB_TEST_EXPORT_HEADERS = [
  '비교 단계',
  '퍼소나명',
  '퍼소나 태그',
  '퍼소나 정보',
  '선택안',
  'A 화면',
  'B 화면',
  '선택 이유/피드백',
  '오류 메시지',
];

const AB_TEST_SUMMARY_HEADERS = ['항목', '내용'];

const formatWinnerLabel = (winner) => (winner === 'tie' ? '동률' : `${winner}안`);
const getABPersonaTagText = (persona = {}) => (
  persona.tag ||
  persona.personaTag ||
  persona.persona_tag ||
  ''
);
const getABPersonaInfoText = (persona = {}) => [
  persona.gender,
  persona.age,
  persona.role || persona.title || persona.occupation,
].filter(Boolean).join(' / ');

const buildABTestExportData = ({ test, results, personas, flowSteps, summary, isFlowResult }) => {
  const rows = [];
  const summaryRows = [
    { '항목': '테스트명', '내용': test?.name || '' },
    { '항목': '테스트 유형', '내용': `${isFlowResult ? '사용자 플로우' : '단일화면'} / ${getSourceTypeLabel(test)}` },
    { '항목': '전체 우승안', '내용': formatWinnerLabel(summary.winner) },
    { '항목': '총 투표', '내용': summary.totalVotes },
    { '항목': 'A 득표', '내용': summary.voteA },
    { '항목': 'B 득표', '내용': summary.voteB },
    { '항목': 'A 비율', '내용': `${summary.percentA}%` },
    { '항목': 'B 비율', '내용': `${summary.percentB}%` },
    { '항목': '목적/설명', '내용': test?.purpose || test?.description || getContextData(test)?.description || '' },
  ];
  results.forEach((result) => {
    const persona = getPersonaSnapshot(result, personas);
    const winner = getResultWinner(result);
    flowSteps.forEach((step, index) => {
      rows.push({
        '비교 단계': isFlowResult ? `Flow ${index + 1}` : '단일화면',
        '퍼소나명': persona.name || '',
        '퍼소나 태그': getABPersonaTagText(persona),
        '퍼소나 정보': getABPersonaInfoText(persona),
        '선택안': formatWinnerLabel(winner),
        'A 화면': step.a?.name || step.a?.label || 'A 화면',
        'B 화면': step.b?.name || step.b?.label || 'B 화면',
        '선택 이유/피드백': getFeedbackText(result),
        '오류 메시지': result.error_message || result.errorMessage || '',
      });
    });
  });

  return {
    metaHeaders: AB_TEST_SUMMARY_HEADERS,
    metaRows: summaryRows,
    headers: AB_TEST_EXPORT_HEADERS,
    rows,
    sheets: [
      { name: '요약', headers: AB_TEST_SUMMARY_HEADERS, rows: summaryRows },
      { name: '퍼소나별 선택 결과', headers: AB_TEST_EXPORT_HEADERS, rows },
    ],
  };
};

function AuthenticatedABImage({ src, alt, fallbackText }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const resolvedSrc = useMemo(() => personaAssetUrl(src), [src]);

  useEffect(() => {
    let cancelled = false;
    let nextObjectUrl = '';

    setFailed(false);
    setObjectUrl('');

    if (!src || !isPersonaStorageImage(src)) {
      return undefined;
    }

    apiFetch(src, { method: 'GET' })
      .then((response) => {
        if (!response.ok) throw new Error(`이미지 요청 실패: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [src]);

  const displaySrc = isPersonaStorageImage(src) ? objectUrl : resolvedSrc;
  if (!displaySrc || failed) {
    return (
      <div className="ab-result-screen-empty">
        <strong>화면 이미지를 불러오지 못했습니다</strong>
        <p>{fallbackText || '연결된 이미지 소스가 없습니다.'}</p>
      </div>
    );
  }

  return <img src={displaySrc} alt={alt || ''} onError={() => setFailed(true)} />;
}

function ModalFrame({ title, description, children, footer, onClose, className = '' }) {
  return (
    <div className="ui-result-modal-backdrop" role="presentation">
      <section
        className={`ui-result-modal${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ab-result-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="ab-result-modal-title">{title}</h2>
          {description ? <p>{description}</p> : null}
        </header>
        <div className="ui-result-modal__body">{children}</div>
        <footer>{footer}</footer>
      </section>
    </div>
  );
}

function DeleteTestModal({ deleting, onCancel, onConfirm }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ab-test-delete-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="ab-test-delete-modal-title">선택한 테스트를 삭제하시겠습니까?</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>삭제 시 관련 해당 테스트의 정보는 모두 삭제되며</p>
          <p>이 작업은 취소할 수 없습니다.</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={deleting}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}>
            {deleting ? '삭제 중' : '삭제하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function PersonaInfoRows({ title, rows }) {
  const displayRows = asArray(rows).filter(([, value]) => asDetailText(value, '') !== '');
  return (
    <section className="ui-result-persona-info-card">
      <h3>{title}</h3>
      <dl>
        {displayRows.length ? displayRows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{asDetailText(value)}</dd>
          </div>
        )) : (
          <div>
            <dt>정보</dt>
            <dd>-</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

function PersonaInfoModal({ persona, onClose }) {
  const detail = buildPersonaDetail(persona) || {};
  const basicRows = asArray(detail.basic).filter(([label]) => ['나이', '세대', '성별'].includes(label));
  return (
    <ModalFrame
      title="퍼소나 기본정보"
      onClose={onClose}
      className="ui-result-modal--persona-info"
      footer={<button type="button" onClick={onClose}>확인</button>}
    >
      <div className="ui-result-persona-info">
        <div className="ui-result-persona-info__profile">
          <PersonaAvatar persona={persona} size="md" />
          <div>
            <strong>{persona?.name || '퍼소나'}</strong>
            {persona?.tag ? <PersonaTagChip tag={persona.tag} /> : null}
          </div>
        </div>
        <div className="ui-result-persona-info__grid">
          <PersonaInfoRows title="기본 정보" rows={basicRows} />
          <PersonaInfoRows title={detail.roleSectionTitle || '직업 정보'} rows={detail.job} />
        </div>
      </div>
    </ModalFrame>
  );
}

function VoteDonut({ summary }) {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const percentA = Math.max(0, Math.min(100, summary.percentA || 0));
  const percentB = Math.max(0, Math.min(100, summary.percentB || 0));
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const bArc = (percentB / 100) * circumference;
  const aArc = (percentA / 100) * circumference;
  const preferred = summary.winner === 'A' ? 'A' : summary.winner === 'B' ? 'B' : '';
  const tooltip = hoveredSegment === 'A'
    ? { label: 'A안', percent: percentA, votes: summary.voteA }
    : hoveredSegment === 'B'
      ? { label: 'B안', percent: percentB, votes: summary.voteB }
      : null;

  return (
    <aside className="ab-result-vote-card">
      <div className="ab-result-donut">
        <svg className="ab-result-donut__chart" viewBox="0 0 144 144" aria-hidden="true">
          <circle
            className="ab-result-donut__track"
            cx="72"
            cy="72"
            r={radius}
            onMouseEnter={() => setHoveredSegment('A')}
            onMouseLeave={() => setHoveredSegment(null)}
          />
          {percentB > 0 ? (
            <circle
              className="ab-result-donut__value"
              cx="72"
              cy="72"
              r={radius}
              strokeDasharray={`${bArc} ${circumference - bArc}`}
              strokeDashoffset={-aArc}
              onMouseEnter={() => setHoveredSegment('B')}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          ) : null}
        </svg>
        {tooltip ? (
          <div className={`ab-result-donut__tooltip ab-result-donut__tooltip--${hoveredSegment.toLowerCase()}`} role="tooltip">
            <strong>{tooltip.label}</strong>
            <span>{`${tooltip.percent}%`}</span>
            <em>{`${tooltip.votes}표`}</em>
          </div>
        ) : null}
        <span className="ab-result-donut__label ab-result-donut__label--a">
          <b>A</b>
        </span>
        <span className="ab-result-donut__label ab-result-donut__label--b">
          <b>B</b>
        </span>
      </div>
      <dl>
        <div className={preferred === 'A' ? 'is-preferred' : ''}>
          <dt><i />A안</dt>
          <dd>{summary.voteA}표</dd>
        </div>
        <div className={preferred === 'B' ? 'is-preferred' : ''}>
          <dt><i />B안</dt>
          <dd>{summary.voteB}표</dd>
        </div>
      </dl>
    </aside>
  );
}

function ABScreenPanel({ variant, screen, preferred }) {
  const isPreferred = preferred === variant;
  return (
    <article className={`ab-result-screen-panel is-variant-${variant.toLowerCase()}${isPreferred ? ' is-preferred' : ''}`}>
      <header>
        <strong>{variant} 화면</strong>
        {isPreferred ? <span>선호</span> : null}
      </header>
      <div>
        <AuthenticatedABImage
          src={screen?.image_url}
          alt={screen?.name || `${variant} 화면`}
          fallbackText={screen?.url || screen?.name || `${variant} 화면`}
        />
      </div>
    </article>
  );
}

function ABTestDetailPage({ shareMode = false }) {
  const navigate = useNavigate();
  const { abTestId, shareToken } = useParams();
  const isShareView = shareMode || Boolean(shareToken);
  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFlowIndex, setActiveFlowIndex] = useState(0);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedPersonaInfo, setSelectedPersonaInfo] = useState(null);
  const [shareLink, setShareLink] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'success' });
  const [opinionPage, setOpinionPage] = useState(0);
  const [exportFormat, setExportFormat] = useState('csv');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isShareView) {
        const shared = await personaResultShareApi.getShare(shareToken);
        if (shared?.resourceType !== 'ab_test') throw new Error('공유 링크를 찾을 수 없습니다.');
        const sharedTest = shared.result || {};
        setTest(sharedTest);
        setResults(asArray(sharedTest.results));
        setPersonas([]);
        return;
      }
      const [testData, resultData, personaData] = await Promise.all([
        personaAbTestApi.getTest(abTestId),
        personaAbTestApi.listResults(abTestId),
        personaApi.listPersonas({ limit: 100 }),
      ]);
      const mergedResults = asArray(resultData).length ? resultData : asArray(testData?.results);
      setTest(testData);
      setResults(mergedResults);
      setPersonas(personaData.items || []);
    } catch (err) {
      setError(isShareView ? '공유 링크를 찾을 수 없습니다.' : (err.message || 'A/B 테스트 정보를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [abTestId, isShareView, shareToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const variantScreensA = useMemo(() => getVariantScreens(test, 'A'), [test]);
  const variantScreensB = useMemo(() => getVariantScreens(test, 'B'), [test]);
  const flowSteps = useMemo(() => getFlowSteps(variantScreensA, variantScreensB), [variantScreensA, variantScreensB]);
  const summary = useMemo(() => getSummary(test, results), [results, test]);
  const isFlowResult = test?.mode === 'flow' || getSourceData(test).scope_type === 'flow';
  const activeStep = flowSteps[activeFlowIndex] || flowSteps[0] || {};
  const shareUrl = shareLink || (typeof window !== 'undefined' ? window.location.href : '');

  const personaOpinions = useMemo(() => (
    results.map((result) => {
      const persona = getPersonaSnapshot(result, personas);
      const winner = getResultWinner(result);
      return {
        id: result.id,
        persona,
        winner,
        feedback: getFeedbackText(result),
      };
    })
  ), [personas, results]);
  const opinionPageCount = Math.max(1, Math.ceil(personaOpinions.length / FEEDBACK_PAGE_SIZE));
  const normalizedOpinionPage = Math.min(opinionPage, opinionPageCount - 1);
  const visiblePersonaOpinions = personaOpinions.slice(
    normalizedOpinionPage * FEEDBACK_PAGE_SIZE,
    normalizedOpinionPage * FEEDBACK_PAGE_SIZE + FEEDBACK_PAGE_SIZE
  );

  useEffect(() => {
    if (opinionPage > opinionPageCount - 1) {
      setOpinionPage(Math.max(0, opinionPageCount - 1));
    }
  }, [opinionPage, opinionPageCount]);

  useEffect(() => {
    if (activeFlowIndex >= flowSteps.length) setActiveFlowIndex(0);
  }, [activeFlowIndex, flowSteps.length]);

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast({ isOpen: true, message: '공유 링크가 복사되었습니다.', type: 'success' });
    } catch (err) {
      setError('공유 링크 복사에 실패했습니다.');
    }
  };

  const handleOpenShare = async () => {
    try {
      const share = await personaResultShareApi.createShare('ab_test', abTestId);
      setShareLink(share.shareUrl || '');
      setModal('share');
    } catch (err) {
      setError(err.message || '공유 링크를 생성하지 못했습니다.');
    }
  };

  const handleExport = () => {
    const exportData = buildABTestExportData({
      test,
      results,
      personas,
      flowSteps,
      summary,
      isFlowResult,
    });
    downloadExportFile({
      filename: sanitizeCsvFilename(test?.name, 'ab-test-result'),
      format: exportFormat,
      ...exportData,
    });
    setModal(null);
  };

  const handleOpenPersonaInfo = (persona) => {
    setSelectedPersonaInfo(persona);
    setModal('persona-info');
  };

  const handleClosePersonaInfo = () => {
    setSelectedPersonaInfo(null);
    setModal(null);
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await personaAbTestApi.deleteTest(abTestId);
      navigate('/tests?tab=ab', {
        state: { toast: { message: '테스트를 삭제했습니다.', type: 'success' } },
      });
    } catch (err) {
      setError(err.message || 'A/B 테스트 삭제에 실패했습니다.');
      setDeleting(false);
    }
  };

  const PageShell = ({ children }) => (
    isShareView ? (
      <>{children}</>
    ) : (
      <B2BLayout hideCollapseButton={true} defaultCollapsed={true} hidePageHeader={true}>
        {children}
      </B2BLayout>
    )
  );
  const pageClassName = `ui-result-page${isShareView ? ' share-view' : ''}`;

  if (loading) {
    return (
      <PageShell>
        <main className={pageClassName}>
          <PersonaState type="loading" title="불러오는 중" message="A/B 비교검증 결과를 가져오고 있습니다." />
        </main>
      </PageShell>
    );
  }

  if (!test) {
    return (
      <PageShell>
        <main className={pageClassName}>
          <ErrorBanner message={error} />
          <PersonaState
            title={isShareView ? '공유 링크를 찾을 수 없습니다' : '테스트를 찾을 수 없습니다'}
            action={isShareView ? null : <button type="button" className="persona-secondary-button" onClick={() => navigate('/tests?tab=ab')}>목록으로</button>}
          />
        </main>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <main className={pageClassName}>
        {!isShareView ? (
          <PersonaResultHeader title="A/B 비교검증 결과" onBack={() => navigate('/tests?tab=ab')} />
        ) : null}

        <section className="ui-result-body ab-result-body">
          <div className="ui-result-content ab-result-content">
            <ErrorBanner message={error || test.error_message} />
            <div className="ui-result-summary">
              <div>
                <h2>{test.name}</h2>
                <div className="ui-result-meta">
                  <span>A/B 비교검증</span>
                  <ChevronRight size={16} />
                  <span>{isFlowResult ? '사용자 플로우' : '단일화면'}</span>
                  <ChevronRight size={16} />
                  <span>{getSourceTypeLabel(test)}</span>
                  <i />
                  <UserRound size={16} />
                  <em>{getPersonaCount(test, results)}명</em>
                  <i />
                  <em>{formatResultDate(test.created_at)}</em>
                </div>
              </div>
              {!isShareView ? (
                <div className="ui-result-actions">
                  <button type="button" onClick={handleOpenShare}><LinkIcon size={18} />공유</button>
                  <button type="button" onClick={() => setModal('export')}><Download size={18} />내보내기</button>
                  <button type="button" onClick={() => setModal('delete')}><Trash2 size={18} />삭제</button>
                </div>
              ) : null}
            </div>
            <div className="ui-result-divider" />

            <section className="ab-result-section">
              <h3>평가 결과</h3>
              <div className="ab-result-evaluation-card">
                <VoteDonut summary={summary} />
                <section className="ab-result-opinions">
                  <div className="ui-result-section__head">
                    <h4>퍼소나 의견</h4>
                    {personaOpinions.length > FEEDBACK_PAGE_SIZE ? (
                      <div className="ui-result-feedback-pagination" aria-label="퍼소나 의견 페이지">
                        <button
                          type="button"
                          onClick={() => setOpinionPage((page) => Math.max(0, page - 1))}
                          disabled={normalizedOpinionPage === 0}
                          aria-label="이전 퍼소나 의견"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <div>
                          {Array.from({ length: opinionPageCount }).map((_, index) => (
                            <button
                              key={index}
                              type="button"
                              className={index === normalizedOpinionPage ? 'active' : ''}
                              onClick={() => setOpinionPage(index)}
                              aria-label={`퍼소나 의견 ${index + 1}페이지`}
                              aria-current={index === normalizedOpinionPage ? 'page' : undefined}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setOpinionPage((page) => Math.min(opinionPageCount - 1, page + 1))}
                          disabled={normalizedOpinionPage >= opinionPageCount - 1}
                          aria-label="다음 퍼소나 의견"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {personaOpinions.length ? (
                    <div>
                      {visiblePersonaOpinions.map((item) => (
                        <article key={item.id}>
                          <div className="ab-result-persona">
                            <PersonaAvatar persona={item.persona} size="sm" />
                            <strong>{item.persona.name}</strong>
                            <button
                              type="button"
                              onClick={() => handleOpenPersonaInfo(item.persona)}
                              aria-label={`${item.persona.name || '퍼소나'} 상세 보기`}
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                          <p>{item.feedback}</p>
                          <span className={[
                            item.winner === 'A' ? 'is-variant-a' : '',
                            item.winner === 'B' ? 'is-variant-b' : '',
                            item.winner === summary.winner ? 'is-preferred' : '',
                          ].filter(Boolean).join(' ')}>
                            {item.winner === 'tie' ? '동률' : `${item.winner}안 선택`}
                          </span>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <PersonaState title="퍼소나 의견이 없습니다" message="A/B 비교검증을 실행하면 퍼소나별 선택 이유가 표시됩니다." />
                  )}
                </section>
              </div>
            </section>

            <section className="ab-result-section">
              <h3>{isFlowResult ? '단계별 비교화면' : '비교화면'}</h3>
              {isFlowResult ? (
                <nav className="ui-result-flow-steps ab-result-flow-steps" aria-label="플로우 화면 선택">
                  {flowSteps.map((step, index) => (
                    <React.Fragment key={step.label}>
                      <button
                        type="button"
                        className={activeFlowIndex === index ? 'active' : ''}
                        onClick={() => setActiveFlowIndex(index)}
                      >
                        <span>
                          <AuthenticatedABImage src={step.b?.image_url || step.a?.image_url} alt="" fallbackText="" />
                        </span>
                        <strong>{step.label}</strong>
                      </button>
                      {index < flowSteps.length - 1 ? <i aria-hidden="true" /> : null}
                    </React.Fragment>
                  ))}
                </nav>
              ) : null}
              <div className="ab-result-comparison">
                <ABScreenPanel variant="A" screen={activeStep.a} preferred={summary.winner} />
                <ABScreenPanel variant="B" screen={activeStep.b} preferred={summary.winner} />
              </div>
            </section>
          </div>
        </section>

        {modal === 'share' ? (
          <ModalFrame
            title="공유하기"
            description="아래 링크를 통해 접속하면 누구나 테스트 결과를 볼 수 있습니다."
            onClose={() => setModal(null)}
            footer={<button type="button" onClick={() => setModal(null)}>확인</button>}
          >
            <label className="ui-result-share-field">
              <span>공유링크</span>
              <div>
                <input value={shareUrl} readOnly />
                <button type="button" onClick={handleCopyShareUrl} aria-label="공유 링크 복사">
                  <Copy size={20} />
                </button>
              </div>
            </label>
            <small>{`${formatSharedAt()}에 공유됨`}</small>
          </ModalFrame>
        ) : null}

        {modal === 'export' ? (
          <ModalFrame
            title="내보내기"
            description="내보낼 파일 형식을 선택해주세요."
            onClose={() => setModal(null)}
            footer={(
              <>
                <button type="button" onClick={() => setModal(null)}>취소하기</button>
                <button type="button" onClick={handleExport}>내보내기</button>
              </>
            )}
          >
            <div className="ui-result-export-options">
              {EXPORT_OPTIONS.map((option) => (
                <label key={option.value} className={exportFormat === option.value ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="abExportFormat"
                    value={option.value}
                    checked={exportFormat === option.value}
                    onChange={(event) => setExportFormat(event.target.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </ModalFrame>
        ) : null}

        {modal === 'delete' ? (
          <DeleteTestModal
            deleting={deleting}
            onCancel={() => {
              if (deleting) return;
              setModal(null);
            }}
            onConfirm={handleDelete}
          />
        ) : null}
        {modal === 'persona-info' && selectedPersonaInfo ? (
          <PersonaInfoModal persona={selectedPersonaInfo} onClose={handleClosePersonaInfo} />
        ) : null}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
        />
      </main>
    </PageShell>
  );
}

export default ABTestDetailPage;
